import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { adFree } from "@/lib/pro";
import { LangProvider } from "@/lib/i18n";
import { AuthCallback } from "@/components/AuthCallback";
import LoginPage from "@/pages/LoginPage";
import AccountPage from "@/pages/AccountPage";
import TermsPage from "@/pages/TermsPage";
import { DisclaimerGate } from "@/components/Disclaimer";
import { HelpBot } from "@/components/HelpBot";
import { SessionProvider } from "@/context/SessionContext";
import { BottomTabs } from "@/components/BottomTabs";
import { AdBanner } from "@/components/AdBanner";
import { MobileHelpers } from "@/components/MobileHelpers";
import { NearbyBar } from "@/components/NearbyBar";
import { ArrivalBar } from "@/components/ArrivalBar";
import { OfflineSync } from "@/components/OfflineSync";
import FindPage from "@/pages/FindPage";
import DropPage from "@/pages/DropPage";
import ChartPage from "@/pages/ChartPage";
import TanksPage from "@/pages/TanksPage";
import SitesPage from "@/pages/SitesPage";
import PaymentPage from "@/pages/PaymentPage";
import MorePage from "@/pages/MorePage";
import ReviewsPage from "@/pages/ReviewsPage";
import LoadPage from "@/pages/LoadPage";
import UstLookupPage from "@/pages/UstLookupPage";
import MeasurePage from "@/pages/MeasurePage";
import VerifyPage from "@/pages/VerifyPage";
import ChartPrintPage from "@/pages/ChartPrintPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AboutPage from "@/pages/AboutPage";
import DonatePage from "@/pages/DonatePage";
import GuidePage from "@/pages/GuidePage";
import FaqPage from "@/pages/FaqPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { useEffect, useLayoutEffect, useState } from "react";
import { GATE_FREE, termsAccepted } from "@/components/Disclaimer";

const NO_TABS = ["/sites", "/login", "/account", "/terms", "/payment/success", "/payment/cancel", "/chart/print", "/privacy", "/about", "/guide", "/faq"];
const AD_PAGES = ["/", "/about", "/reviews", "/guide", "/faq"];

function Shell() {
  const { pathname, hash } = useLocation();
  const { user } = useAuth();
  const [agreed, setAgreed] = useState(termsAccepted);
  useEffect(() => {
    const on = () => setAgreed(true);
    window.addEventListener("terms-accepted", on);
    return () => window.removeEventListener("terms-accepted", on);
  }, []);
  const showAd = AD_PAGES.includes(pathname) && !adFree(user) && (agreed || GATE_FREE.includes(pathname));
  useEffect(() => { if (!showAd) document.body.classList.add("no-ad"); }, [showAd]);
  // Leaving an ad page for a screen without ads: reload so no ad script, slot or frame carries over.
  useLayoutEffect(() => {
    if (!AD_PAGES.includes(pathname) && document.querySelector('script[src*="adsbygoogle"], ins.adsbygoogle, iframe[src*="googlesyndication"], iframe[id^="aswift"]')) window.location.reload();
  }, [pathname]);
  if (hash?.includes("session_id=")) return <AuthCallback />;
  const tabs = !NO_TABS.includes(pathname);
  return (
    <div className={`app-column ${tabs ? "" : "no-tabs"}`}>
      <MobileHelpers />
      <OfflineSync />
      {(pathname === "/" || pathname === "/drop") && <NearbyBar />}
      {(pathname === "/" || pathname === "/drop") && <ArrivalBar />}
      <main className="app-main" key={pathname}>
        <Routes>
          <Route path="/" element={<FindPage />} />
          <Route path="/drop" element={<DropPage />} />
          <Route path="/chart" element={<ChartPage />} />
          <Route path="/tanks" element={<TanksPage />} />
          <Route path="/more" element={<MorePage />} />
          <Route path="/sites" element={<SitesPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/ust" element={<UstLookupPage />} />
          <Route path="/measure" element={<MeasurePage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/chart/print" element={<ChartPrintPage />} />
          <Route path="/load" element={<LoadPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/donate" element={<DonatePage />} />
          <Route path="/payment/success" element={<PaymentPage />} />
          <Route path="/payment/cancel" element={<PaymentPage cancelled />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      {showAd && <AdBanner key={`ad-${pathname}`} />}
      {tabs && <BottomTabs />}
      {pathname !== "/chart/print" && <HelpBot />}
      <DisclaimerGate />
    </div>
  );
}

export default function App() {
  return (
    <LangProvider>
      <BrowserRouter>
        <AuthProvider>
          <SessionProvider>
            <Shell />
          </SessionProvider>
        </AuthProvider>
      </BrowserRouter>
    </LangProvider>
  );
}
